import { useMemo, useSyncExternalStore } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { AlertSourceState } from '@/features/conversations/lib/alert-sources';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { getOrder } from '../api/orders.api';
import type { OrderDetail } from '../types/orders.types';
import { ordersKeys } from './useOrders';
import { useOrdersSignalR, type OrderAmendedEvent } from './useOrdersSignalR';

/* ==========================================================================
   Fonte `amended_on_the_road` dos alertas globais (change
   orders-customer-amendments, decisão 7; spec "Pagamento alterado com o
   pedido na rua")

   O cliente trocou a forma de pagamento ou o troco com o pedido em rota: o
   `order.amended` chega com `deliveryAlreadyOut`, e a loja precisa falar com o
   entregador. Em qualquer tela, para quem vê pedidos: "Pedido #42: o cliente
   trocou a forma de pagamento com o pedido na rua. Avise o entregador." com
   "Ver pedido", o som de solicitação uma vez (a fonte não repete) e
   "Entendi", que encerra o aviso nesta aba. O aviso também sai quando o
   pedido deixa a rua. O evento não traz o número do dia: o detalhe do pedido
   (que quem vê pedidos já lê) dá o número e o status, e nada do cliente vai
   para a faixa nem para a notificação. O card e o detalhe mantêm o destaque
   para quem perdeu o aviso.
   ========================================================================== */

export const AMENDED_ON_THE_ROAD_ALERT_SOURCE_ID = 'amended_on_the_road';

export const AMENDED_ON_THE_ROAD_NOTIFICATION_TITLE = 'Pagamento trocado na rua';

interface RoadNotice {
  /** A loja selecionada quando o evento chegou: o aviso só vale nela. */
  tenantId: string;
  orderId: string;
  amendmentNumber: number;
  /** A chegada do evento nesta aba, a marca do alerta. */
  receivedAt: number;
}

/* ---- Os avisos desta aba, fora do React: o "Entendi" da faixa os encerra. ---- */

let notices: readonly RoadNotice[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/* A troca de loja e a saída encerram os avisos: eram da loja anterior, o pedido
   dela não existe na outra (a leitura do detalhe daria 404) e o aviso voltaria
   ao retornar a ela. */
useAuthStore.subscribe((state, previous) => {
  if ((state.selectedTenant?.id ?? null) !== (previous.selectedTenant?.id ?? null)) {
    dismissAmendedOnTheRoadNotices();
  }
});

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getNotices(): readonly RoadNotice[] {
  return notices;
}

/** Guarda o aviso do evento; outra alteração do mesmo pedido substitui a anterior. */
export function recordAmendedOnTheRoad(event: OrderAmendedEvent, receivedAt = Date.now()): void {
  const tenantId = useAuthStore.getState().selectedTenant?.id;
  if (!event?.deliveryAlreadyOut || !event.orderId || !tenantId) return;
  notices = [
    ...notices.filter((notice) => notice.orderId !== event.orderId),
    { tenantId, orderId: event.orderId, amendmentNumber: event.amendmentNumber, receivedAt },
  ];
  emit();
}

/** "Entendi": encerra os avisos desta aba. */
export function dismissAmendedOnTheRoadNotices(): void {
  if (notices.length === 0) return;
  notices = [];
  emit();
}

/** "Pedido #42", ou o código de rastreio no pedido anterior à numeração. */
function orderLabel(order: Pick<OrderDetail, 'dailyNumber' | 'trackingCode'>): string {
  return order.dailyNumber != null ? `Pedido #${order.dailyNumber}` : `Pedido ${order.trackingCode}`;
}

function orderHref(orderId: string): string {
  return `/orders?pedido=${encodeURIComponent(orderId)}`;
}

export interface RoadNoticeView {
  orderId: string;
  amendmentNumber: number;
  receivedAt: number;
  order: Pick<OrderDetail, 'dailyNumber' | 'trackingCode' | 'status'>;
}

/** O estado da fonte a partir dos avisos com o pedido lido; só os de pedido ainda em rota contam. */
export function amendedOnTheRoadAlertState(views: readonly RoadNoticeView[]): AlertSourceState {
  const onTheRoad = views.filter((view) => view.order.status === 'out_for_delivery');
  const marks = onTheRoad.map((view) => view.receivedAt);
  const latestMark = marks.length > 0 ? Math.max(...marks) : null;
  const newest = onTheRoad.find((view) => view.receivedAt === latestMark);

  let line = '';
  if (onTheRoad.length === 1) {
    line = `${orderLabel(onTheRoad[0].order)}: o cliente trocou a forma de pagamento com o pedido na rua. Avise o entregador.`;
  }
  if (onTheRoad.length > 1) {
    line = `${onTheRoad.length} pedidos com a forma de pagamento trocada na rua. Avise os entregadores.`;
  }

  return {
    count: onTheRoad.length,
    highPriorityCount: 0,
    latestMark,
    line,
    note: null,
    link:
      onTheRoad.length === 1
        ? { to: orderHref(onTheRoad[0].orderId), label: 'Ver pedido' }
        : { to: '/orders', label: 'Ver pedidos' },
    dismissLabel: onTheRoad.length > 0 ? 'Entendi' : null,
    notification: newest
      ? {
          title: AMENDED_ON_THE_ROAD_NOTIFICATION_TITLE,
          body: `${orderLabel(newest.order)} · avise o entregador`,
          tag: `amended-on-the-road-${newest.orderId}-${newest.amendmentNumber}`,
          href: orderHref(newest.orderId),
        }
      : null,
  };
}

/**
 * O `useAlertState` da fonte. Só é chamado pelo `ConversationAlerts` com
 * `orders.read` e a área do Atendente IA disponível (a alteração só nasce da
 * conversa com ele). Sem aviso, a contagem é 0, e a primeira leitura é a
 * linha de base: o aviso que chega depois toca uma vez.
 */
export function useAmendedOnTheRoadAlertState(): AlertSourceState | null {
  // O ouvinte é a função do módulo: a mesma referência em toda renderização.
  useOrdersSignalR({ onAmended: recordAmendedOnTheRoad });
  const tenantId = useAuthStore((s) => s.selectedTenant?.id ?? '');
  const all = useSyncExternalStore(subscribe, getNotices, getNotices);
  const current = useMemo(() => all.filter((notice) => notice.tenantId === tenantId), [all, tenantId]);
  const details = useQueries({
    queries: current.map((notice) => ({
      queryKey: ordersKeys.detail(tenantId, notice.orderId),
      queryFn: () => getOrder(notice.orderId),
      enabled: !!tenantId,
    })),
  });

  const views: RoadNoticeView[] = [];
  current.forEach((notice, index) => {
    const order = details[index]?.data;
    if (order) views.push({ ...notice, order });
  });
  return amendedOnTheRoadAlertState(views);
}

/** Só para os testes. */
export function resetAmendedOnTheRoadNotices(): void {
  notices = [];
  emit();
}
